<script setup lang="ts">
import { hasOnlyPlacement } from "~/utils/self-assessment-placement";
import { Delta, Quill } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";
import {
  SelectionType,
  SelfAssessmentStep,
  WordSelectionMode,
} from "~/classes/models/self-assessment/self-assessment-step.model";
import { RevisionEditHistory } from "~/classes/models/submitted-documents/revision-history-edit.model";
import { useAttributeInstances } from "~/composables/quill-selection/useAttributeInstances";
import { useCountWords } from "~/composables/quill-selection/useCountWords";
import { useGetRangeFromAttrValue } from "~/composables/quill-selection/useGetRangeFromAttrValue";
import {
  useParagraphSentences,
  useSentenceOpenerStart,
} from "~/composables/quill-selection/useParagraphSentences";
import { useRemoveAttributeFromRanges } from "~/composables/quill-selection/useRemoveAttributeFromRanges";
import { useSelfAssessmentValidateInstance } from "~/composables/selfAssessmentValidation/useSelAssessmentValidateInstance";
import { useConvertImportedHtmlToQuillDelta } from "~/composables/useConvertImportedHtmlToQuillDelta";
import { useTrimmedSelectionRange } from "~/composables/useTrimmedSelectionRange";
import { useBannedWordChecks } from "~/composables/useBannedWordChecks";
import { Revision } from "~/types/Revision";
import { createDocumentHistoryCheckpointScheduler } from "~/utils/document-history-checkpoint-scheduler";
import { useWordProcessorStore } from "../Base/WordProcessor/useWordProcessorStore";
const Parchment = Quill.import("parchment");

//

const documentId = inject("documentId") as any;

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument, revision, isEditingMode } = storeToRefs(
  submittedDocumentStore
);
const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isCompanionMode, isStudentViewMode } = storeToRefs(documentViewModeStore);
const canSaveCheckpoint = computed(
  () => isCompanionMode.value || isStudentViewMode.value
);
const documentRevisionEditHistoryStore =
  useDocumentRevisionEditHistoryStore(documentId);

const wordProcessorInstanceId = documentId;
const wordProcessorStore = useWordProcessorStore(wordProcessorInstanceId);
const { selectionRange, editorRef } = storeToRefs(wordProcessorStore);
const restoreDocumentId = documentId?.value ?? documentId;
const { isApplyingRestore } =
  useDocumentEditingHistoryRestoreState(restoreDocumentId);

const thesaurusStore = useDocumentThesaurusStore(documentId);

const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const {
  stepCriteriaAttributes,
  activeStep,
  allParagraphs,
  allSteps,
  allSentences,
  bodyParagraphs,
  stepParagraphPositions,
  quillDelta,
  bannedWordsInstances,
  bannedWords,
  stepRelevantInstances,
} = storeToRefs(documentSelfAssessmentStore);

const lastUpdated = ref(0);
const isInitializingEditorContent = ref(false);
const bannedWordEditor = shallowRef<any>(null);
const isSavingCheckpoint = ref(false);

const getTrimmedSelectionRange = useTrimmedSelectionRange();

const customAttributors = computed(() => {
  return [
    ...stepCriteriaAttributes.value,
    ...[
      {
        attribute: "data-banned-word",
        scope: Parchment.Scope.INLINE,
        documentSelectionType: SelectionType.token,
      },
      {
        attribute: "data-highlight",
        scope: Parchment.Scope.INLINE,
        documentSelectionType: SelectionType.token,
      },
      {
        attribute: "data-highlight-block",
        scope: Parchment.Scope.BLOCK,
        documentSelectionType: SelectionType.paragraph,
      },
      {
        attribute: TEACHER_COMMENT_ATTRIBUTE,
        scope: Parchment.Scope.INLINE,
        documentSelectionType: SelectionType.token,
      },
    ],
  ];
});

const onPaste = (event: ClipboardEvent) => {};

/** Create an empty document whose first line and subsequent typing are double-spaced. */
const createDefaultEmptyDelta = () =>
  new Delta([{ insert: "\n", attributes: { lineheight: "2" } }]);

const NON_BREAKING_SPACE_REGEX = /[\u00A0\u202F\u2007]/g;

const normalizeNonBreakingSpaces = (value: string) => {
  return value.replace(NON_BREAKING_SPACE_REGEX, " ");
};

const sanitizeOpsNonBreakingSpaces = (ops: any) => {
  if (!Array.isArray(ops)) {
    return {
      nextOps: ops,
      changed: false,
    };
  }

  let changed = false;
  const nextOps = ops.map((op: any) => {
    if (typeof op?.insert !== "string") {
      return op;
    }

    const sanitizedInsert = normalizeNonBreakingSpaces(op.insert);
    if (sanitizedInsert === op.insert) {
      return op;
    }

    changed = true;
    return {
      ...op,
      insert: sanitizedInsert,
    };
  });

  return {
    nextOps,
    changed,
  };
};

const isDeltaEmpty = (delta: Delta) => {
  const ops = delta.ops;
  if (!ops || ops.length === 0) return true;
  if (
    ops.length === 1 &&
    ops[0].insert === "\n" &&
    !(ops[0].attributes && (ops[0].attributes as any).lineheight)
  )
    return true;
  return false;
};

const cloneOps = (ops: any) => {
  return JSON.parse(JSON.stringify(ops ?? []));
};

const areOpsEqual = (left: any, right: any) => {
  return JSON.stringify(left ?? []) === JSON.stringify(right ?? []);
};

const cloneHeaders = (headers: Revision["headers"]) => {
  if (headers == undefined) return undefined;

  return JSON.parse(JSON.stringify(headers));
};

const areHeadersEqual = (
  left: Revision["headers"],
  right: Revision["headers"]
) => {
  return JSON.stringify(left ?? []) === JSON.stringify(right ?? []);
};

const onWordProcessorReady = async () => {
  isInitializingEditorContent.value = true;
  let shouldPersistSanitizedOps = false;

  try {
    quillDelta.value = createDefaultEmptyDelta();

    if (!revision.value) return;

    if (revision.value.studentSubmissionOps == undefined) {
      await loadFromParagraphs();

      if (isDeltaEmpty(quillDelta.value)) {
        quillDelta.value = createDefaultEmptyDelta();
        revision.value.studentSubmissionOps = quillDelta.value.ops;
      }
    } else {
      quillDelta.value = new Delta(revision.value.studentSubmissionOps);
      if (isDeltaEmpty(quillDelta.value)) {
        quillDelta.value = createDefaultEmptyDelta();
        revision.value.studentSubmissionOps = quillDelta.value.ops;
      }
    }

    const { nextOps, changed } = sanitizeOpsNonBreakingSpaces(
      revision.value.studentSubmissionOps
    );
    if (changed) {
      revision.value.studentSubmissionOps = nextOps;
      quillDelta.value = new Delta(nextOps);
      shouldPersistSanitizedOps = true;
    }

    const quill = editorRef.value?.getQuill?.();
    if (quill) {
      // The revision is loaded after Quill's ready event, so synchronize the
      // mounted editor explicitly instead of relying on a later prop update.
      quill.setContents(quillDelta.value, Quill.sources.SILENT);
      documentSelfAssessmentStore.setQuillEditor();
      bannedWordEditor.value = quill;

      const clipboard = quill.getModule("clipboard");
      clipboard.addMatcher(
        Node.ELEMENT_NODE,
        (node: HTMLElement, delta: Delta) => {
          const newOps = delta.ops.map((op: any) => {
            if (op.attributes) {
              const cleanedAttributes = { ...op.attributes };
              Object.keys(cleanedAttributes).forEach((key) => {
                if (key.startsWith("data-")) {
                  delete cleanedAttributes[key];
                }
              });
              return { ...op, attributes: cleanedAttributes };
            }
            return op;
          });
          return new Delta(newOps);
        }
      );

      quill.root.addEventListener("paste", onPaste);
    }

    await nextTick();

    checkForBannedWords();

    lastUpdated.value = Date.now();

    if (quill) {
      quill.history.clear();
    }

    if (shouldPersistSanitizedOps) {
      await saveRevision();
    }

    thesaurusStore.thesaurusEventEmitter.subscribe((action) => {
      if (action.event == ThesaurusAction.replaceWord) {
        const text = quill.getText(
          action.data.documentText.startIndex,
          action.data.documentText.endIndex -
            action.data.documentText.startIndex
        );
        if (
          text.trim().toLowerCase() !=
          action.data.documentText.text.trim().toLowerCase()
        ) {
          useBaseToast("Oops! The text can't be replaced.", "error");
          return;
        }

        const currentFormatting = quill.getFormat(
          action.data.documentText.startIndex,
          action.data.documentText.endIndex -
            action.data.documentText.startIndex
        );

        quill.deleteText(
          action.data.documentText.startIndex,
          action.data.documentText.endIndex -
            action.data.documentText.startIndex
        );
        quill.insertText(
          action.data.documentText.startIndex,
          action.data.word,
          Quill.sources.USER
        );
        quill.formatText(
          action.data.documentText.startIndex,
          action.data.word.length,
          currentFormatting
        );

        // Move the cursor to the end of the inserted text
        quill.setSelection(
          action.data.documentText.startIndex + action.data.word.length,
          0,
          Quill.sources.SILENT
        );
      }
    });
  } finally {
    isInitializingEditorContent.value = false;
  }
};

const summarizeImportedDelta = (delta: Delta) => {
  const ops = delta?.ops ?? [];

  return {
    opCount: ops.length,
    underlineOps: ops.filter((op: any) => op?.attributes?.underline === true)
      .length,
    strikeOps: ops.filter((op: any) => op?.attributes?.strike === true).length,
    tabStringOps: ops.filter(
      (op: any) =>
        typeof op?.insert === "string" &&
        (op.insert.includes("\t") || op.insert.includes("\u200B"))
    ).length,
    previewOps: ops.slice(0, 8),
  };
};

const loadFromParagraphs = async () => {
  if (!revision.value) return;

  if (revision.value.html) {
    const quill = editorRef.value?.getQuill?.();
    if (!quill) return;

    const delta = useConvertImportedHtmlToQuillDelta(
      quill,
      revision.value.html
    );

    quillDelta.value = delta;

    revision.value.studentSubmissionOps = quillDelta.value.ops;
    // revision.value.html = undefined;
    saveRevision();
    return;
  }

  const db = useFirestore();
  const paragraphsRef = collection(
    db,
    `/documents/${documentId}/revisions/${revision.value.id}/paragraphs`
  );
  const paragraphsQuery = query(
    paragraphsRef,
    orderBy("paragraphPosition", "asc")
  );
  const paragraphsSnapshot = await getDocs(paragraphsQuery);

  const paragraphs = paragraphsSnapshot.docs.map((doc) => {
    return {
      ...doc.data(),
      id: doc.id,
    } as Paragraph;
  });

  quillDelta.value = useConvertParagraphsToDelta(paragraphs);

  revision.value.studentSubmissionOps = quillDelta.value.ops;
};

const quillHtml = ref();
const toHtml = useConvertQuillDeltaToHtml();

const saveRevision = async () => {
  if (!revision.value) return;

  await submittedDocumentStore.saveRevision(revision.value, true);
};

const saveDocumentEditingHistory = async (
  studentSubmissionOps: any,
  headers?: Revision["headers"]
) => {
  if (!revision.value) return;

  const revisionId = revision.value.id;

  if (revisionId == undefined) return;

  const userId = useCurrentUID();

  if (userId == undefined) return;

  if (documentId == undefined) return;

  const documentEditingHistory = new RevisionEditHistory({
    documentId: documentId,
    revisionId: revisionId,
    studentSubmissionOps: studentSubmissionOps,
    headers: cloneHeaders(headers ?? revision.value.headers),
    lastUpdatedTimestamp: Date.now(),
    editedByUserId: userId,
    editedByUserName: submittedDocument.value?.studentName,
  });

  await documentRevisionEditHistoryStore.saveEditHistory(
    documentEditingHistory
  );
};

const _saveDocumentEditingHistoryDebounced = debounce(
  saveDocumentEditingHistory,
  500
);

const _saveRevisionThrottled = throttle(saveRevision, 1000);

const persistCheckpoint = async () => {
  if (!revision.value?.id) {
    throw new Error("Unable to find the revision being saved.");
  }

  _saveRevisionThrottled.cancel();
  _saveDocumentEditingHistoryDebounced.cancel();

  const checkpointTimestamp = Date.now();
  const checkpointOps = cloneOps(revision.value.studentSubmissionOps ?? []);
  const checkpointHeaders = cloneHeaders(revision.value.headers);
  const checkpointRevision: Revision = {
    ...revision.value,
    studentSubmissionOps: checkpointOps,
    headers: checkpointHeaders,
    lastUpdatedTimestamp: checkpointTimestamp,
  };
  await submittedDocumentStore.saveRevision(checkpointRevision, true);

  const userId = useCurrentUID();
  if (userId == undefined) {
    throw new Error("Unable to identify the user saving this checkpoint.");
  }

  const checkpoint = new RevisionEditHistory({
    documentId,
    revisionId: revision.value.id,
    studentSubmissionOps: checkpointOps,
    headers: checkpointHeaders,
    lastUpdatedTimestamp: checkpointTimestamp,
    editedByUserId: userId,
    editedByUserName: submittedDocument.value?.studentName,
  });

  await documentRevisionEditHistoryStore.saveCheckpoint(checkpoint);
};

const checkpointScheduler = createDocumentHistoryCheckpointScheduler({
  save: persistCheckpoint,
  onSavingChange: (isSaving) => {
    isSavingCheckpoint.value = isSaving;
  },
  onSaved: (hasPendingChanges) => {
    if (!hasPendingChanges || !revision.value) return;

    _saveRevisionThrottled();
    _saveDocumentEditingHistoryDebounced(
      cloneOps(revision.value.studentSubmissionOps ?? []),
      cloneHeaders(revision.value.headers)
    );
  },
  onError: () => {
    useBaseToast("Oops! Unable to save this checkpoint.", "error");
  },
});

const createCheckpoint = async ({ manual = false } = {}) => {
  if (canSaveCheckpoint.value != true || !revision.value) return;

  const saved = await checkpointScheduler.checkpoint({ force: manual });
  if (saved && manual) {
    useBaseToast("Checkpoint saved.", "success");
  }
};

const markCheckpointDirty = () => {
  if (canSaveCheckpoint.value != true) return;

  checkpointScheduler.markDirty();
};

onBeforeUnmount(() => {
  checkpointScheduler.dispose();
});

watch(quillDelta, () => {
  if (!revision.value) return;

  if (isInitializingEditorContent.value == true) {
    quillHtml.value = toHtml(quillDelta.value);
    onSelectionChange();

    return;
  }

  const nextOps = quillDelta.value.ops;

  if (
    revision.value.studentSubmissionOps != undefined &&
    areOpsEqual(revision.value.studentSubmissionOps, nextOps)
  ) {
    quillHtml.value = toHtml(quillDelta.value);
    onSelectionChange();
    return;
  }

  revision.value.studentSubmissionOps = nextOps;

  if (isApplyingRestore.value != true) {
    markCheckpointDirty();
    _saveRevisionThrottled();
    _saveDocumentEditingHistoryDebounced(revision.value.studentSubmissionOps);
  }

  quillHtml.value = toHtml(quillDelta.value);
  onSelectionChange();
});

const selectedCriteriaAttribute = computed(() => {
  if (activeStep.value == undefined) return undefined;
  return stepCriteriaAttributes.value.find(
    (attribute) =>
      attribute.attribute == activeStep.value?.criteriaAttribute.attribute
  );
});

watch(activeStep, () => {
  lastUpdated.value = Date.now();
  // Clear the quill selection
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  quill.blur();

  if (activeStep.value == undefined) return;

  useAutoApplyInstances({
    documentId,
    step: activeStep.value,
    editorRef: editorRef.value,
    quillDelta: quillDelta.value,
  });
});

const selectedKey = computed(() => {
  return `${numberOfSelectedInstances.value}-${lastUpdated.value}-${selectedCriteriaAttribute.value?.attribute}`;
});

const onRemoveInstance = (
  instanceId: string,
  options?: {
    stepOverride?: SelfAssessmentStep;
    attributeOverride?: string;
  }
) => {
  let step = options?.stepOverride ?? activeStep.value;
  let attribute =
    options?.attributeOverride ?? selectedCriteriaAttribute?.value?.attribute;

  if (step == undefined || attribute == undefined) return;

  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  const elements = document.querySelectorAll(`[${attribute}="${instanceId}"]`);
  for (const element of elements) {
    element.classList.remove("instance-error");
  }

  let attributesToRemove = [attribute];

  if (
    step?.validationAttributes != undefined &&
    Object.keys(step.validationAttributes).length > 0
  ) {
    var duplicateAttributePrefix = step.duplicateAttributePrefix;
    var hasDuplicateAttribute = Array.from(elements).some(
      (element: Element) => {
        if (duplicateAttributePrefix == undefined) return false;

        const dataAttributes = element.getAttributeNames().filter((name) => {
          return name.startsWith(duplicateAttributePrefix!);
        });

        return dataAttributes.length > 1;
      }
    );

    if (step.validationAttributes.bold == true) {
      attributesToRemove.push("bold");
    }

    if (
      step.validationAttributes.underline == true &&
      hasDuplicateAttribute != true
    ) {
      attributesToRemove.push("underline");
    }

    if (step.validationAttributes.italic == true) {
      attributesToRemove.push("italic");
    }
  }

  if (step.sentenceOpenerMarkerValidation != undefined) {
    const ranges = useGetRangeFromAttrValue(
      quillDelta.value,
      attribute,
      instanceId
    );

    var relevantInstances = useAttributeInstances(
      quill,
      quillDelta.value.ops,
      attribute
    );

    const instance = relevantInstances.find((instance: any) => {
      return instance.value == instanceId;
    });

    if (instance != undefined && instance.paragraphIndex != undefined) {
      // Find the sentences that contain the ranges
      const sentences = useParagraphSentences(
        quillDelta.value.ops,
        quill,
        instance.paragraphIndex
      );

      const matchingSentences = sentences.filter((sentence) => {
        return ranges.some((range) => {
          return (
            sentence.start <= range.index &&
            sentence.end >= range.index + range.length
          );
        });
      });

      // Process in reverse to avoid index shifting issues when deleting
      const matchingSentencesReversed = [...matchingSentences].reverse();

      for (const sentence of matchingSentencesReversed) {
        const sentenceText = sentence.text;
        const markerRegex = /^\s*([\[\(\{]\d+[\]\)\}])/;
        const match = sentenceText.match(markerRegex);

        if (match) {
          const fullMatch = match[0];
          const markerOnly = match[1];
          const leadingWhitespaceLen = fullMatch.length - markerOnly.length;

          // Where the marker itself starts within the *sentence* (ignoring the leading spaces in the match)
          const markerOffsetInSentence = leadingWhitespaceLen;

          // Now convert to a Quill index
          const markerIndexInDoc = sentence.start + markerOffsetInSentence;
          let lengthToDelete = markerOnly.length;

          // Check if there is a space after the marker
          if (
            match.index !== undefined &&
            sentenceText[match.index + fullMatch.length] === " "
          ) {
            lengthToDelete += 1;
          }

          quill.deleteText(markerIndexInDoc, lengthToDelete, "user");
        }
      }
    }
  }

  useRemoveAttributeFromRanges(
    quill,
    quillDelta.value,
    attribute,
    instanceId,
    attributesToRemove
  );
};

const numberOfSelectedInstances = computed(() => {
  if (selectedCriteriaAttribute.value == undefined) return 0;

  if (editorRef.value == undefined) {
    return 0;
  }

  const quill = editorRef.value?.getQuill?.();
  if (!quill) {
    return 0;
  }

  return useAttributeInstances(
    quill,
    quillDelta.value.ops,
    selectedCriteriaAttribute.value.attribute
  ).length;
});

const selectionCoordinates = ref<
  { x: number; y: number; isSelected: boolean } | undefined
>(undefined);

const doesSelectionContainSelectedCriteriaAttribute = () => {
  if (selectedCriteriaAttribute.value == undefined) return false;

  const quill = editorRef.value?.getQuill?.();
  if (!quill) return false;

  const selectionRange = quill.getSelection();

  if (!selectionRange) return false;

  const format = quill.getFormat(selectionRange.index, selectionRange.length);

  const formatKeys = Object.keys(format);

  return formatKeys.includes(selectedCriteriaAttribute.value.attribute);
};

const lastSavedState = ref<
  | {
      text: string;
      attributes: { [key: string]: any };
      start: number;
      end: number;
    }
  | undefined
>(undefined);

watch(selectionRange, () => {
  onSelectionChange();
});

const stepsWithFormatting = (formatting: string) => {
  return allSteps.value.filter((step: SelfAssessmentStep) => {
    return Object.keys(step.validationAttributes ?? {}).includes(formatting);
  });
};

const onUpdateContent = (content: Delta) => {
  quillDelta.value = content;
  lastUpdated.value = Date.now();

  const quill = editorRef.value?.getQuill?.();

  if (!quill) return;

  // Check if hte current selection matches the last saved state
  const currentSelection = quill.getSelection();
  if (!currentSelection) return;

  const currentSelectionEnd = currentSelection.length + currentSelection.index;
  const lastSavedSelectionEnd = lastSavedState.value?.end;

  if (
    currentSelection.index != lastSavedState.value?.start &&
    currentSelectionEnd != lastSavedSelectionEnd
  )
    return;

  const lastSavedAttributes = lastSavedState.value?.attributes;
  const currentAttributes = quill.getFormat(
    currentSelection.index,
    currentSelection.length
  );

  if (lastSavedAttributes == undefined) return;
  if (currentAttributes == undefined) return;

  let steps = [] as SelfAssessmentStep[];

  if (
    lastSavedAttributes["underline"] == true &&
    currentAttributes["underline"] == undefined
  ) {
    steps = [...steps, ...stepsWithFormatting("underline")];
  }

  if (
    lastSavedAttributes["bold"] == true &&
    currentAttributes["bold"] == undefined
  ) {
    steps = [...steps, ...stepsWithFormatting("bold")];
  }

  if (
    lastSavedAttributes["background"] != undefined &&
    lastSavedAttributes["background"] != null &&
    lastSavedAttributes["background"] != "transparent" &&
    (currentAttributes["background"] == undefined ||
      currentAttributes["background"] == null ||
      currentAttributes["background"] == "transparent")
  ) {
    steps = [...steps, ...stepsWithFormatting("background")];
  }

  if (
    lastSavedAttributes["italic"] == true &&
    currentAttributes["italic"] == undefined
  ) {
    steps = [...steps, ...stepsWithFormatting("italic")];
  }

  for (const step of steps) {
    const criteriaAttribute = step.criteriaAttribute;
    const attribute = criteriaAttribute.attribute;

    const instances = useAttributeInstances(
      quill,
      quillDelta.value.ops,
      attribute
    );

    for (const instance of instances) {
      if (
        instance.start >= currentSelection.index &&
        instance.end <= currentSelection.index + currentSelection.length
      ) {
        onRemoveInstance(instance.value, {
          stepOverride: step,
          attributeOverride: criteriaAttribute.attribute,
        });
      }
    }
  }
};

const editorSelectorToolbarCoordinates = ref<
  | {
      x: number;
      y: number;
      attribute: string | undefined;
      includeDressupSelector: boolean;
      includeSearchButton: boolean;
      includeSentenceOpenerSelector: boolean;
      text: string;
      startIndex: number;
      endIndex: number;
    }
  | undefined
>(undefined);

const onSelectionChange = () => {
  const quill = useWordProcessorStore(documentId).getQuillInstance();
  if (!quill) return;

  const range = quill.getSelection();

  if (!range) return;

  lastSavedState.value = {
    text: quill.getText(range.index, range.length),
    attributes: quill.getFormat(range.index, range.length),
    start: range.index,
    end: range.index + range.length,
  };

  selectionCoordinates.value = undefined;
  editorSelectorToolbarCoordinates.value = undefined;
  // Get the x/y coordinates of the first piece of text in the selection range

  const start = range.index;

  const boundsX = quill.getBounds(start, 1);
  const boundsY = quill.getBounds(start + 1, 1);

  if (!boundsX || !boundsY) return;

  // If we're in student editing mode not sutdent review mode
  if (
    submittedDocument.value?.state == DocumentState.editing ||
    submittedDocument.value?.state == DocumentState.draft
  ) {
    const trimmed = getTrimmedSelectionRange(
      start,
      start + Math.max(range.length, 1),
      quill
    );

    if (!trimmed) return;

    const trimmedLength = trimmed.trimmedEnd - trimmed.trimmedStart;
    const selectedRangeAttributes = quill.getFormat(
      trimmed.trimmedStart,
      trimmedLength
    );

    const attributeKeys = Object.keys(selectedRangeAttributes);

    const includeDressupSelector =
      attributeKeys.includes("underline") && trimmed.text.length > 1;

    const headerElement = document.querySelector(".header-container");

    let headerHeight = 0;
    if (headerElement) {
      headerHeight = headerElement.getBoundingClientRect().height;
    }

    const trimmedBounds = quill.getBounds(trimmed.trimmedStart, 1);

    const sentence = allSentences.value.find(
      (s: any) =>
        s.start <= trimmed.trimmedStart && trimmed.trimmedStart < s.end
    );
    const sentenceOpenerStart = sentence
      ? useSentenceOpenerStart(sentence)
      : undefined;
    const isAtSentenceStart =
      !!sentence &&
      (trimmed.trimmedStart === sentence.start ||
        trimmed.trimmedStart === sentenceOpenerStart);

    editorSelectorToolbarCoordinates.value = {
      x: trimmedBounds?.left ?? boundsX.left,
      y: (trimmedBounds?.top ?? boundsY.top) + headerHeight,
      attribute: attributeKeys.find((attribute) =>
        attribute.toLowerCase().includes("dressup")
      ),
      includeDressupSelector: includeDressupSelector,
      includeSearchButton: trimmed.text.length > 1,
      includeSentenceOpenerSelector: isAtSentenceStart,
      text: trimmed.text,
      startIndex: trimmed.trimmedStart,
      endIndex: trimmed.trimmedEnd,
    };
  }

  if (range.length == 0) return;

  // Selection text length
  const selectionText = quill.getText(start, range.length);
  if (selectionText.trim().length == 0) return;

  if (selectedCriteriaAttribute.value == undefined) return;

  // Check if the range contains the selected criteria attribute
  let isSelected = false;
  const selectedInstances = useAttributeInstances(
    quill,
    quillDelta.value.ops,
    selectedCriteriaAttribute.value.attribute
  );
  if (selectedInstances.length > 0) {
    isSelected = true;
  }

  if (doesSelectionContainSelectedCriteriaAttribute() != true) {
    const headerElement = document.querySelector(".header-container");

    let headerHeight = 0;
    if (headerElement) {
      headerHeight = headerElement.getBoundingClientRect().height;
    }

    let shortcutContainerHeight = 0;
    const shortcutContainer = document.querySelector(".shortcut-container");
    if (shortcutContainer) {
      shortcutContainerHeight =
        shortcutContainer.getBoundingClientRect().height;
    }

    selectionCoordinates.value = {
      x: boundsX.left,
      y: boundsY.top + headerHeight,
      isSelected: isSelected,
    };
  }
};

const applySentenceOpenerAttribute = async ({
  selectionRange,
  step,
}: {
  selectionRange: {
    index: number;
    length: number;
  };
  step: SelfAssessmentStep;
}) => {
  const quill = wordProcessorStore.getQuillInstance();

  if (!quill) return;

  // First let's get the paragraph from the current selection range

  const paragraph = allParagraphs.value.find((paragraph: any) => {
    var selectionRangeStart = selectionRange.index;
    var selectionRangeEnd = selectionRange.index + selectionRange.length;
    return (
      paragraph.start <= selectionRangeStart &&
      paragraph.end >= selectionRangeEnd
    );
  });

  if (paragraph) {
    let paragraphIndex = allParagraphs.value.indexOf(paragraph);

    const sentences = useParagraphSentences(
      quillDelta.value.ops,
      quill,
      paragraphIndex
    );

    const matchingSentence = sentences.find((sentence) => {
      var selectionRangeStart = selectionRange.index;
      var selectionRangeEnd = selectionRangeStart + selectionRange.length;
      return (
        sentence.start <= selectionRangeStart &&
        sentence.end >= selectionRangeEnd
      );
    });

    if (matchingSentence) {
      var sentenceStart = matchingSentence.start;
      var sentenceEnd = matchingSentence.end;
      quill.setSelection(sentenceStart, sentenceEnd - sentenceStart);

      // First check if the sentence contains any attributes that start with "data-sentence-opener"
      const attributes = quill.getFormat(
        sentenceStart,
        sentenceEnd - sentenceStart
      );
      const attributesToRemove = Object.keys(attributes).filter((attribute) => {
        return attribute.startsWith("data-sentence-opener");
      });

      for (const attribute of attributesToRemove) {
        quill.format(attribute, null);
      }

      // Oh right htat's cool so that epxansds the seleciotn to the entire sentence. Now we can check if the sentence contains the thing we need.
      if (step.sentenceOpenerMarkerValidation != undefined) {
        // Check if the sentence text contains the marker
        const sentenceText = matchingSentence.text;
        const markerRegex = /^\s*([\[\(\{]\d+[\]\)\}])/;

        const expectedMarker = step.sentenceOpenerMarkerValidation ?? "";
        const match = sentenceText.match(markerRegex);

        const startsWithMarker = !!match;
        const startsWithCorrectMarker = sentenceText
          .replace(/^\s+/, "") // strip leading spaces
          .startsWith(expectedMarker);

        if (startsWithMarker == true && startsWithCorrectMarker != true) {
          // We need to replace the current marker with the correct one.
          const fullMatch = match[0]; // e.g. "   [3]"
          const markerOnly = match[1]; // e.g. "[3]"
          const leadingWhitespaceLen = fullMatch.length - markerOnly.length;

          // Where the marker itself starts within the *sentence* (ignoring the leading spaces in the match)
          const markerOffsetInSentence = leadingWhitespaceLen;

          // Now convert to a Quill index (you must already know sentenceStartIndex)
          const markerIndexInDoc = sentenceStart + markerOffsetInSentence;
          const markerLength = markerOnly.length;

          quill.deleteText(markerIndexInDoc, markerLength, "user");
          quill.insertText(markerIndexInDoc, expectedMarker, "user");
          // Remove all formatting from the inserted text to ensure it doesn't inherit formatting
          // quill.removeFormat(markerIndexInDoc, expectedMarker.length, "user");
          const markerFormat = quill.getFormat(
            markerIndexInDoc,
            expectedMarker.length
          );
          const markerFormatAttributes = Object.keys(markerFormat).filter(
            (attribute) => {
              return attribute != step.criteriaAttribute.attribute;
            }
          );

          for (const format of markerFormatAttributes) {
            quill.formatText(
              markerIndexInDoc,
              expectedMarker.length,
              format,
              false,
              "user"
            );
          }
        }

        if (startsWithMarker != true) {
          // We need to insert the correct marker at the beginning of the sentence.
          const insertedText = expectedMarker + " ";

          quill.insertText(sentenceStart, insertedText, "user");
          // Remove all formatting from the inserted text to ensure it doesn't inherit formatting
          // quill.removeFormat(sentenceStart, insertedText.length, "user");
          const markerFormat = quill.getFormat(
            sentenceStart,
            insertedText.length
          );

          const markerFormatAttributes = Object.keys(markerFormat).filter(
            (attribute) => {
              return attribute != step.criteriaAttribute.attribute;
            }
          );

          for (const format of markerFormatAttributes) {
            quill.formatText(
              sentenceStart,
              insertedText.length,
              format,
              false,
              "user"
            );
          }
        }
      }
    }
  }
};

const applyAttributeToSelection = async () => {
  if (selectedCriteriaAttribute.value == undefined) return;

  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  const selectionRange = quill.getSelection();
  if (!selectionRange) return;

  const instanceId = useGenerateUID();

  // OK so the first thing we want to do is if the current step is a sentence, we want to expand the selection to the sentence boundaries. we'll start with just that. Then we can work on a regex to find and replace markers or insert a marker in.

  if (activeStep.value?.selectionType == SelectionType.sentence) {
    // First let's get the paragraph from the current selection range

    await applySentenceOpenerAttribute({
      selectionRange,
      step: activeStep.value,
    });
  }

  // First we need to trim the selection range.
  const currentSelection = quill.getSelection();

  if (currentSelection) {
    const selectedText = quill.getText(
      currentSelection.index,
      currentSelection.length
    );

    if (selectedText.trim().length == 0) return;

    const startOffset = selectedText.search(/\S/);

    if (startOffset !== -1) {
      const trimmedLength = selectedText.trim().length;
      quill.setSelection(currentSelection.index + startOffset, trimmedLength);
    }
  }

  quill.format(selectedCriteriaAttribute.value.attribute, instanceId);

  selectionCoordinates.value = undefined;

  var relevantInstances = useAttributeInstances(
    quill,
    quillDelta.value.ops,
    selectedCriteriaAttribute.value.attribute
  );

  var relevantInstance = relevantInstances.find((instance: any) => {
    return instance.value == instanceId;
  });

  if (relevantInstance == undefined) return;

  if (activeStep.value == undefined) return;

  let instances = [relevantInstance];

  const wordCount = useCountWords(relevantInstance.textSnippet ?? "");

  if (
    wordCount > 1 &&
    activeStep.value.wordSelectionMode == WordSelectionMode.autoSplit
  ) {
    // Remove the attribute from the selection.
    onRemoveInstance(instanceId);

    const textSnippet = relevantInstance.textSnippet ?? "";
    const matches = textSnippet.matchAll(/\S+/g);

    instances = [];
    let newInstanceIds = [];

    // Now we need to apply the attribute to each word as a sub range of the original instance.
    for (const match of matches) {
      const word = match[0];
      const wordOffset = match.index;

      if (wordOffset !== undefined) {
        const newInstanceId = useGenerateUID();
        quill.formatText(
          relevantInstance.start + wordOffset,
          word.length,
          selectedCriteriaAttribute.value.attribute,
          newInstanceId
        );
        newInstanceIds.push(newInstanceId);
      }
    }

    var relevantInstances = useAttributeInstances(
      quill,
      quillDelta.value.ops,
      selectedCriteriaAttribute.value.attribute
    );

    instances = relevantInstances.filter((instance: any) => {
      return newInstanceIds.includes(instance.value);
    });
  }

  for (const relevantInstance of instances) {
    let hasFormattingError = false;
    try {
      await useSelfAssessmentValidateInstance(
        activeStep.value,
        relevantInstance,
        bodyParagraphs.value,
        stepParagraphPositions.value(activeStep.value),
        quillDelta.value.ops,
        quill
      );
    } catch (error: any) {
      if (error.message == "formatting-error") {
        // Recheck after applying supported automatic formatting below.
        hasFormattingError = true;
      }

      if (error.message == "duplicate-attribute-error") {
        activeStep.value.errorMessage =
          activeStep.value.errorMessageMap?.["duplicate-attribute-error"] ??
          "Oops! You did not pass the assessment.";

        continue;
      }

      if (error.message == "multiple-word-instances-error") {
        activeStep.value.errorMessage =
          activeStep.value.errorMessageMap?.["multiple-word-instances-error"] ??
          "For each selection, choose one word only.";

        continue;
      }

      if (error.message == "placement-error") {
        activeStep.value.errorMessage =
          activeStep.value.errorMessageMap?.["placement-error"] ??
          "Oops! You did not pass the assessment.";

        if (hasOnlyPlacement(activeStep.value, "topicClinchers")) {
          activeStep.value.errorMessage =
            "Oops! Your selected words must be in the first sentence and the last sentence of each body paragraph.";
        }

        continue;
      }
    }

    var validationAttributes = activeStep.value?.validationAttributes;
    if (validationAttributes != undefined) {
      // Apply bold, underline, italics to the selection if it exist

      var start = relevantInstance.start;
      var length = relevantInstance.length;
      var text = relevantInstance.textSnippet;

      // Make sure the start and length represent the trimmed instance.
      if (text?.startsWith(" ")) {
        start += 1;
        length -= 1;
      }

      if (text?.endsWith(" ")) {
        length -= 1;
      }

      if (validationAttributes.bold == true) {
        quill.formatText(start, length, "bold", true);
      }

      if (validationAttributes.italic == true) {
        quill.formatText(start, length, "italic", true);
      }

      if (validationAttributes.underline == true) {
        quill.formatText(start, length, "underline", true);
      }
    }

    if (hasFormattingError) {
      const updatedInstance = useAttributeInstances(
        quill,
        quill.getContents().ops,
        activeStep.value.criteriaAttribute.attribute
      ).find((instance) => instance.value === relevantInstance.value);

      if (updatedInstance) {
        try {
          useSelfAssessmentValidateInstance(
            activeStep.value,
            updatedInstance,
            bodyParagraphs.value,
            stepParagraphPositions.value(activeStep.value),
            quill.getContents().ops,
            quill
          );
        } catch (error: any) {
          activeStep.value.errorMessage =
            activeStep.value.errorMessageMap?.[error.message] ??
            "Oops! You did not pass the assessment.";
        }
      }
    }
  }

  // Blur the editor
  quill.blur();
};

const removeAttributeFromSelection = () => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  // Focus the editor
  quill.focus();

  if (selectedCriteriaAttribute.value == undefined) return;

  const selectionRange = quill.getSelection();
  if (!selectionRange) return;

  const format = quill.getFormat(selectionRange.index, selectionRange.length);
  const existingInstanceId = format[selectedCriteriaAttribute.value.attribute];

  if (existingInstanceId == undefined) return;

  onRemoveInstance(existingInstanceId);
};

useBannedWordChecks(bannedWordEditor, bannedWords, (quill) => {
  documentSelfAssessmentStore.checkForBannedWords(quill);
});

const onKeyDown = (event: KeyboardEvent) => {
  if (
    event.code === "KeyS" &&
    (event.ctrlKey || event.metaKey) &&
    canSaveCheckpoint.value == true
  ) {
    event.preventDefault();
    event.stopPropagation();
    void createCheckpoint({ manual: true });

    return false;
  }

  if (event.code === "KeyD" && (event.ctrlKey || event.metaKey)) {
    if (doesSelectionContainSelectedCriteriaAttribute()) {
      removeAttributeFromSelection();
    } else {
      applyAttributeToSelection();
    }

    event.preventDefault();
    event.stopPropagation();

    return false;
  }

  if (event.isComposing) return;

  if (event.key === '"' || event.key === "'") {
    const quill = editorRef.value?.getQuill?.();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const format = quill.getFormat(selection.index, selection.length);
    if (format["code"] || format["code-block"]) return;

    event.preventDefault();
    event.stopPropagation();

    let textBefore = "";
    if (selection.index > 0) {
      textBefore = quill.getText(selection.index - 1, 1);
    }

    const isDouble = event.key === '"';
    const isOpening =
      selection.index === 0 || /[\s\(\[\{\u2013\u2014]/.test(textBefore);

    let quoteChar = "";
    if (isDouble) {
      quoteChar = isOpening ? "“" : "”";
    } else {
      quoteChar = isOpening ? "‘" : "’";
    }

    if (selection.length > 0) {
      quill.deleteText(selection.index, selection.length, "user");
    }
    quill.insertText(selection.index, quoteChar, "user");
    quill.setSelection(selection.index + 1, 0, "user");

    return false;
  }
};

const modifierKey = computed(() => {
  if (typeof window === "undefined") return "Ctrl";
  const platform = navigator.platform || navigator.userAgent;
  const isMac = /Mac|iPhone|iPod|iPad/i.test(platform);
  return isMac ? "⌘" : "Ctrl";
});

const relevantInstances = computed(() => {
  if (activeStep.value == undefined) return [];

  return stepRelevantInstances.value(activeStep.value);
});

const checkForBannedWords = () => {
  const quill = bannedWordEditor.value ?? wordProcessorStore.getQuillInstance();
  if (!quill) return;

  documentSelfAssessmentStore.checkForBannedWords(quill);
};

const onUpdateHeader = (header: any) => {
  if (!revision.value) return;

  const nextHeaders =
    header != undefined
      ? [
          {
            alignment: header.alignment,
            extraText: header.extraText,
            includePageNumbers: header.includePageNumbers,
          },
        ]
      : undefined;

  if (areHeadersEqual(revision.value.headers, nextHeaders)) {
    return;
  }

  revision.value.headers = nextHeaders;
  revision.value.lastUpdatedTimestamp = Date.now();
  markCheckpointDirty();
  submittedDocumentStore.saveRevision(revision.value!);
  saveDocumentEditingHistory(
    cloneOps(revision.value.studentSubmissionOps ?? []),
    nextHeaders
  );
};

const previousBoundaries = ref<{ start: number; end: number } | undefined>(
  undefined
);

const onBlur = () => {
  selectionCoordinates.value = undefined;
  editorSelectorToolbarCoordinates.value = undefined;
};

const dressUpIdentificationPopoverController = useNewDialogController();

const onSelectDressUp = (step: SelfAssessmentStep) => {
  dressUpIdentificationPopoverController.value.close();

  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  // Remove other dress-up attributes
  allSteps.value.forEach((step) => {
    var isDressupAttribute = step.criteriaAttribute.attribute
      .toLowerCase()
      .includes("dressup");
    if (isDressupAttribute) {
      quill.format(step.criteriaAttribute.attribute, false);
    }
  });

  quill.format(step.criteriaAttribute.attribute, useGenerateUID());
};

const onClearDressUp = () => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  quill.format(editorSelectorToolbarCoordinates.value?.attribute, false);
};

const sentenceOpenerPopoverController = useNewDialogController();

const onSelectSentenceOpener = async (step: SelfAssessmentStep) => {
  sentenceOpenerPopoverController.value.close();

  await applySentenceOpenerAttribute({
    selectionRange: {
      index: selectionRange.value?.index ?? 0,
      length: selectionRange.value?.length ?? 0,
    },
    step,
  });
};

const onClearSentenceOpener = () => {
  sentenceOpenerPopoverController.value.close();

  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;
};

const documentState = computed(() => {
  return submittedDocumentStore.submittedDocument?.state;
});

watch(documentState, () => {
  if (documentState.value == DocumentState.editing) {
    documentSelfAssessmentStore.resetSelection();
  } else if (
    documentState.value == DocumentState.studentReview ||
    documentState.value == DocumentState.studentCompleted
  ) {
    documentSelfAssessmentStore.applyStepSelection();
  }
});

const sentenceOpenerSteps = computed(() => {
  return allSteps.value.filter((step) => {
    return step.sentenceOpenerMarkerValidation != undefined;
  });
});

const duplicateCompatibleSteps = computed(() => {
  const duplicateAttributePrefix = activeStep.value?.duplicateAttributePrefix;
  if (duplicateAttributePrefix == undefined) return [];

  return allSteps.value.filter((step) => {
    return step.criteriaAttribute.attribute.startsWith(
      duplicateAttributePrefix
    );
  });
});
</script>

<template>
  <div
    id="document-editor-container"
    class="h-full min-h-0 overflow-hidden flex flex-col
      [&_.instance-error]:!inline
      [&_.instance-error]:relative
      [&_.instance-error]:!py-1
      [&_.instance-error]:!leading-[1.5]
      [&_.instance-error]:!align-baseline
      [&_p.instance-error]:!block
      [&_p.instance-error]:w-fit
      [&_p.instance-error]:!py-0
      [&_p.instance-error.ql-align-center]:mx-auto
      [&_p.instance-error.ql-align-right]:ml-auto"
  >
    <BaseWordProcessorContainer
      v-if="quillDelta != undefined"
      :instance-id="wordProcessorInstanceId"
      :model-value="quillDelta"
      :custom-attributors="customAttributors"
      :header="revision?.headers?.[0]"
      @update:header="onUpdateHeader"
      @on-editor-mounted="onWordProcessorReady"
      @on-update-content="onUpdateContent"
      @on-key-down="onKeyDown"
      @on-blur="onBlur"
    >
      <template #toolbar-actions>
        <button
          v-if="canSaveCheckpoint"
          v-tooltip="'Save checkpoint'"
          type="button"
          aria-label="Save checkpoint"
          class="!flex !float-none !h-[30px] !w-[30px] shrink-0 items-center justify-center rounded !p-0 text-primary-default transition-colors hover:bg-medium-default/10 disabled:cursor-default disabled:text-medium-tint"
          :aria-disabled="isSavingCheckpoint"
          :disabled="isSavingCheckpoint"
          @mousedown.prevent.stop
          @click.prevent.stop="createCheckpoint({ manual: true })"
        >
          <icon
            v-if="isSavingCheckpoint"
            name="material-symbols:sync"
            size="16"
            class="!float-none !h-4 !w-4 shrink-0 animate-spin"
          />
          <icon
            v-else
            name="lucide:save"
            size="16"
            class="!float-none !h-4 !w-4 shrink-0"
          />
        </button>
      </template>
      <template #fixed-header>
        <div
          v-if="activeStep != undefined"
          class="flex flex-row justify-end shortcut-container"
        >
          <div class="p-2 border-b border-dashed w-full">
            <p
              class="shortcut-text text-base flex flex-row items-center justify-end gap-1.5"
            >
              <BasePopover>
                <BaseIconInfo />
                <template #content>
                  <div class="p-4 bg-surface rounded">
                    <p class="mb-2">To select text, highlight it by</p>
                    <ol class="list-disc list-inside mb-2">
                      <li>clicking and dragging your mouse, or</li>
                      <li>double-clicking, or</li>
                      <li>holding Shift and using the arrow keys.</li>
                    </ol>

                    <p class="mb-2">
                      Click the ✓ in the upper left corner to confirm the
                      selection.<br />
                      Click the X if you make a mistake.
                    </p>
                  </div>
                </template>
              </BasePopover>
              <span class="text-selection relative">
                Select your text
                <span class="flex items-center justify-center">
                  <BaseIconCursorCaret size="18" class="mb-[2px]" />
                </span>
              </span>
              <span class="text-muted-foreground">
                Click
                <BaseIconCircleCheck size="18" class="mb-[2px]" />
              </span>
            </p>
          </div>
        </div>
      </template>
      <template #overlays>
        <Transition name="fade">
          <button
            v-if="
              selectionCoordinates &&
              (documentState == DocumentState.studentReview ||
                documentState == DocumentState.studentCompleted)
            "
            class="selection-confirm-button w-4 h-4 flex items-center justify-center pointer-events-auto absolute z-[999999] hover:scale-110 active:scale-95"
            :style="{
              left: selectionCoordinates.x - 5 + 'px',
              top: selectionCoordinates.y - 15 + 'px',
            }"
            @mousedown.stop.prevent="applyAttributeToSelection"
            title="Confirm selection"
          >
            <BaseIconCheck class="text-[8px]" />
          </button>
        </Transition>

        <Transition name="fade">
          <div
            v-if="
              editorSelectorToolbarCoordinates &&
              (documentState == DocumentState.editing ||
                documentState == DocumentState.studentCompleted)
            "
            :key="editorSelectorToolbarCoordinates.attribute"
            :style="{
              left: editorSelectorToolbarCoordinates.x - 5 + 'px',
              top: editorSelectorToolbarCoordinates.y - 15 + 'px',
            }"
            class="absolute z-[999999] flex flex-row gap-1"
          >
            <DocumentEditorLookupWordButton
              v-if="editorSelectorToolbarCoordinates?.includeSearchButton"
              :text="editorSelectorToolbarCoordinates?.text"
              :start-index="editorSelectorToolbarCoordinates?.startIndex"
              :end-index="editorSelectorToolbarCoordinates?.endIndex"
            />
            <BasePopover
              v-if="editorSelectorToolbarCoordinates?.includeDressupSelector"
              align="start"
              :controller="dressUpIdentificationPopoverController"
            >
              <button
                class="bg-surface rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] ring-[1.5px] ring-[rgba(43,80,118,0.2)] w-4 h-4 flex items-center justify-center pointer-events-auto hover:scale-110 active:scale-95"
                v-tooltip="'Identify Dress-Up'"
              >
                <BaseIconChevronCircleDown class="text-[12px]" />
              </button>
              <template #content>
                <DocumentStudentEditingDressupsSelectorOptions
                  @select="onSelectDressUp"
                  :selectedAttribute="
                    editorSelectorToolbarCoordinates?.attribute
                  "
                  @clear="onClearDressUp"
                />
              </template>
            </BasePopover>
            <BasePopover
              v-if="
                editorSelectorToolbarCoordinates?.includeSentenceOpenerSelector &&
                sentenceOpenerSteps.length > 0
              "
              align="start"
              :controller="sentenceOpenerPopoverController"
            >
              <button
                class="bg-surface rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] ring-[1.5px] ring-[rgba(43,80,118,0.2)] w-4 h-4 flex items-center justify-center pointer-events-auto hover:scale-110 active:scale-95"
                v-tooltip="'Identify Sentence Opener'"
              >
                <BaseIconChevronCircleDown class="text-[12px]" />
              </button>
              <template #content>
                <DocumentStudentEditingSentenceOpenersSelectorOptions
                  @select="onSelectSentenceOpener"
                  :selectedAttribute="
                    editorSelectorToolbarCoordinates?.attribute
                  "
                  @clear="onClearSentenceOpener"
                />
              </template>
            </BasePopover>
          </div>
        </Transition>

        <DocumentEditorBannedWords
          :quill="bannedWordEditor"
          :instances="bannedWordsInstances"
        />

        <DocumentSelectionSelectedButtons
          v-if="
            selectedCriteriaAttribute != undefined &&
            (documentState == DocumentState.studentReview ||
              documentState == DocumentState.studentCompleted)
          "
          :quill-delta="quillDelta"
          :selectedDataAttribute="selectedCriteriaAttribute.attribute"
          :selectionType="selectedCriteriaAttribute.documentSelectionType"
          :duplicate-attribute-prefix="activeStep?.duplicateAttributePrefix"
          :duplicate-compatible-steps="duplicateCompatibleSteps"
          quill-editor-selector=".ql-editor"
          document-selector="word-processor-container"
          :scroll-offset-y="0"
          @removeInstance="onRemoveInstance"
          :key="selectedKey"
          :word-processor-instance-id="wordProcessorInstanceId"
        />
      </template>
    </BaseWordProcessorContainer>
  </div>
</template>

<style>
#document-editor-container .word-processor-container u {
  text-decoration-color: #4a7db5 !important;
  text-decoration-thickness: 2.25px !important;
}

#document-editor-container
  .word-processor-container
  .selected-criteria-attribute {
  @apply text-white !important;
  background: #4a7db5 !important;

  padding: 4px 0px !important;
  display: inline;
  line-height: 1.5 !important;
  vertical-align: baseline !important;
  animation: verticalBounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) !important;

  span {
    @apply text-white !important;
  }

  u {
    text-decoration-color: white !important;
  }
}

/* Make paragraphs with this class shrink to fit content */
#document-editor-container
  .word-processor-container
  p.selected-criteria-attribute {
  width: fit-content;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  display: block;

  &.ql-align-center {
    margin-left: auto;
    margin-right: auto;
  }

  &.ql-align-right {
    margin-left: auto;
  }
}

#document-editor-container
  .word-processor-container
  u.selected-criteria-attribute {
  text-decoration-color: white !important;
}

#document-editor-container
  .word-processor-container
  .instance-error {
  @apply bg-danger !important;
  animation: instanceErrorShake 0.5s ease-in-out !important;
  color: white !important;
}

#document-editor-container
  .word-processor-container
  .selected-criteria-attribute.first, .instance-error {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 4px rgba(74, 125, 181, 0.3),
    inset 0 1px 0 rgba(74, 125, 181, 0.4),
    inset 0 -1px 0 rgba(74, 125, 181, 0.4),
    inset 1px 0 0 rgba(74, 125, 181, 0.4) !important;
  z-index: 99998;
  padding-left: 4px !important;
}

#document-editor-container .word-processor-container [data-highlight] {
  background-color: rgba(74, 125, 181, 0.2);
}

#document-editor-container
  .word-processor-container
  .selected-criteria-attribute.last,
  .instance-error {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: 0 2px 4px rgba(74, 125, 181, 0.3),
    inset 0 1px 0 rgba(74, 125, 181, 0.4),
    inset 0 -1px 0 rgba(74, 125, 181, 0.4),
    inset -1px 0 0 rgba(74, 125, 181, 0.4) !important;
  z-index: 99998;
  padding-right: 4px !important;
}

.selected-criteria-attribute, .instance-error {
  /* Base top/bottom shadows for all elements */
  box-shadow: 0 2px 4px rgba(74, 125, 181, 0.3),
    inset 0 1px 0 rgba(74, 125, 181, 0.4),
    inset 0 -1px 0 rgba(74, 125, 181, 0.4) !important;
  z-index: 99997;
}

/* Flutter-inspired elastic bounce animation */
@keyframes elasticBounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  40% {
    transform: scale(1.04);
  }

  60% {
    transform: scale(0.98);
  }

  80% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Vertical-only bounce animation - no horizontal spacing issues */
@keyframes verticalBounceIn {
  0% {
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
  }

  40% {
    padding-top: 4.6px;
    padding-bottom: 4.6px;
  }

  60% {
    padding-top: 3.8px;
    padding-bottom: 3.8px;
  }

  80% {
    padding-top: 4.2px;
    padding-bottom: 4.2px;
  }

  100% {
    padding-top: 4px;
    padding-bottom: 4px;
    opacity: 1;
  }
}

/* Relative offsets preserve inline highlight sizing while shaking. */
@keyframes instanceErrorShake {
  0%,
  100% {
    left: 0;
  }

  10%,
  30%,
  50%,
  70%,
  90% {
    left: -4px;
  }

  20%,
  40%,
  60%,
  80% {
    left: 4px;
  }
}

.selection-confirm-button,
.dress-up-identification-button {
  background: white;
  border-radius: 50%;
  color: #475569;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 0 0 1.5px rgba(43, 80, 118, 0.2);
  border: none;
}

.selection-confirm-button:hover,
.dress-up-identification-button:hover {
  background: #f8fafc;
  color: #1e293b;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(43, 80, 118, 0.3);
}

.selection-confirm-button svg,
.dress-up-identification-button svg {
  position: relative;
  transform: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.shortcut-text {
  font-family: "Indie Flower", cursive;
}

.text-selection {
  font-family: "Indie Flower", cursive;
  display: inline-flex;
  align-items: center;
  background-color: #b3d9ff;
  color: #1a1a1a;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  position: relative;
  font-weight: 500;
}

.selection-caret {
  display: inline-block;
  width: 2px;
  height: 1rem;
  background-color: #1a1a1a;
  margin-left: 2px;
  animation: blink 1s infinite;
  vertical-align: middle;
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}

.keyboard-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  color: #374151;
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  border-bottom-width: 2px;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 1px 0 0 rgba(0, 0, 0, 0.05);
  transition: all 0.1s ease;
}

.keyboard-key:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 0 0 rgba(0, 0, 0, 0.1);
}

[data-banned-word] {
  text-decoration: underline wavy #ffa500;
  text-decoration-thickness: 1.5px;
}
</style>
